import store from '@/store'
import API from '@/services/api'
import { logsService } from '@/services'
import clientId from '@/services/clientId'
import {
  GET_ORDERS,
  GET_ORDER_CART,
  ADD_ORDER,
  ADD_PRODUCT_ORDER,
  ADD_PART_PRODUCT_ORDER,
  REMOVE_PRODUCT_ORDER,
  UPDATE_PRODUCT_ORDER,
  CAN_SEND_ORDER,
  UPDATE_ORDER_NAME,
  UPDATE_ORDER_OBS,
  CLEAR_ORDER,
  DELETE_ORDER,
  CHECKOUT_ORDER,
  GET_ORDER_TRACKING
} from '@/services/enpoints'

function trackCartAdd (event, payload, endpoint, correlation, startedAt, error) {
  const products = Array.isArray(payload.products) ? payload.products : [payload]
  const status = error && error.response && error.response.status

  products.forEach(product => {
    logsService.track({
      event,
      interaction_id: correlation && correlation.interactionId,
      request_id: correlation && correlation.requestId,
      endpoint,
      duration_ms: startedAt ? Date.now() - startedAt : undefined,
      http_status: status,
      error_kind: error && addErrorKind(error),
      cart_id: payload.order,
      product_id: product.id ?? product.product_id,
      quantity: product.quantity,
    })
  })
}

function addErrorEvent (error) {
  if (error.code === 'ECONNABORTED') return 'cart.add.timeout'
  if (!error.response) return 'cart.add.network_error'
  if (error.outcomeUnknown) return 'cart.add.unexpected_error'
  if (error.response.status >= 400 && error.response.status < 500) return 'cart.add.rejected'
  return 'cart.add.unexpected_error'
}

function addErrorKind (error) {
  const status = error.response && error.response.status

  if (error.outcomeUnknown) return 'outcome_unknown'
  if (!error.response) return 'network_or_cors'
  if (status === 401) return 'authentication'
  if (status === 403) return 'authorization'
  if (status === 400 || status === 409 || status === 422) return 'business_rejection'
  if (status >= 500) return 'server'
  return 'frontend_exception'
}

const getAll = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_ORDERS,
    data,
  }).catch((error) => {
    logsService.logs({ error: error })
    throw error
  })
}

const getOrderCart = (data = {}, options = {}) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_ORDER_CART,
    data,
    interactionId: options.interactionId,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const add = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: ADD_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const addProduct = (data) => {
  data.token = store.state.auth.token
  const interactionId = clientId.generateUuidV4()
  const startedAt = Date.now()
  const initialCorrelation = { interactionId }

  trackCartAdd('cart.add.started', data, ADD_PRODUCT_ORDER, initialCorrelation)

  return API.call({
    method: 'POST',
    url: ADD_PRODUCT_ORDER,
    data,
    requiresSessionToken: true,
    interactionId,
    onRequest: correlation => {
      trackCartAdd('cart.add.request_sent', data, ADD_PRODUCT_ORDER, correlation, startedAt)
    },
  }).then(response => {
    trackCartAdd('cart.add.succeeded', data, ADD_PRODUCT_ORDER, response.correlation, startedAt)
    return response
  }).catch(error => {
    trackCartAdd(addErrorEvent(error), data, ADD_PRODUCT_ORDER, error.correlation || initialCorrelation, startedAt, error)
    throw error
  })
}

const addPartProduct = (data) => {
  data.token = store.state.auth.token
  const interactionId = clientId.generateUuidV4()
  const startedAt = Date.now()
  const initialCorrelation = { interactionId }

  trackCartAdd('cart.add.started', data, ADD_PART_PRODUCT_ORDER, initialCorrelation)

  return API.call({
    method: 'POST',
    url: ADD_PART_PRODUCT_ORDER,
    data,
    interactionId,
    onRequest: correlation => {
      trackCartAdd('cart.add.request_sent', data, ADD_PART_PRODUCT_ORDER, correlation, startedAt)
    },
  }).then(response => {
    trackCartAdd('cart.add.succeeded', data, ADD_PART_PRODUCT_ORDER, response.correlation, startedAt)
    return response
  }).catch(error => {
    trackCartAdd(addErrorEvent(error), data, ADD_PART_PRODUCT_ORDER, error.correlation || initialCorrelation, startedAt, error)
    throw error
  })
}

const removeProduct = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: REMOVE_PRODUCT_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const updateProductQuantity = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: UPDATE_PRODUCT_ORDER,
    data,
    requiresSessionToken: true,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const canSend = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CAN_SEND_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const updateOrderName = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: UPDATE_ORDER_NAME,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const updateOrderObs = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: UPDATE_ORDER_OBS,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const clearOrder = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CLEAR_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const remove = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: DELETE_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const checkout = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: CHECKOUT_ORDER,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

const getOrderTracking = (data) => {
  data.token = store.state.auth.token
  return API.call({
    method: 'POST',
    url: GET_ORDER_TRACKING,
    data,
  }).catch(error => {
    logsService.logs({ error: error })
    throw error
  })
}

export const orderService = {
  getAll,
  getOrderCart,
  add,
  addProduct,
  addPartProduct,
  removeProduct,
  updateProductQuantity,
  canSend,
  updateOrderName,
  updateOrderObs,
  clearOrder,
  remove,
  checkout,
  getOrderTracking,
}
